import type React from 'react';
import { useRef } from 'react';
import type { ButtonHTMLAttributes, PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '@/lib/utils';
import { playTapDown, playTapUp } from '@/lib/hooks/useSounds';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'outline' | 'ghost' | 'raised' | 'raisedoutline';
}

const styles: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-brand-600 hover:bg-brand-500 text-white disabled:opacity-60',
  outline: 'border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-700 disabled:opacity-60',
  ghost: 'hover:bg-neutral-100 text-neutral-700 disabled:opacity-60',
  
  // Raised variants - now with slight lightening on hover
  raised: 'btn-raised bg-brand-500 hover:bg-brand-400 text-white disabled:opacity-60',
  raisedoutline: 'btn-raised bg-neutral-0 hover:bg-neutral-50 text-brand-500 disabled:opacity-60',
};

const depthColors: Partial<Record<NonNullable<ButtonProps['variant']>, string>> = {
  raised: '#43a06a',
  raisedoutline: '#d1d5db',
};

export function Button({
  className,
  variant = 'primary',
  style,
  onPointerDown,
  onPointerUp,
  onPointerLeave,
  onPointerCancel,
  onTouchStart,
  onTouchEnd,
  onTouchCancel,
  disabled,
  ...rest
}: ButtonProps) {
  const depth = depthColors[variant ?? 'primary'];
  const depthStyle = depth
    ? ({ '--btn-depth': depth, ...style } as React.CSSProperties)
    : style;

  const pressedRef = useRef(false);

  const handlePointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!disabled && e.pointerType !== 'touch') {
      pressedRef.current = true;
      playTapDown();
    }
    onPointerDown?.(e);
  };

  const handlePointerUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!disabled && e.pointerType !== 'touch' && pressedRef.current) {
      playTapUp();
    }
    pressedRef.current = false;
    onPointerUp?.(e);
  };

  const handlePointerLeave = (e: ReactPointerEvent<HTMLButtonElement>) => {
    pressedRef.current = false;
    onPointerLeave?.(e);
  };

  const handlePointerCancel = (e: ReactPointerEvent<HTMLButtonElement>) => {
    pressedRef.current = false;
    onPointerCancel?.(e);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
    if (!disabled) {
      pressedRef.current = true;
      playTapDown();
    }
    onTouchStart?.(e);
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLButtonElement>) => {
    if (!disabled && pressedRef.current) {
      playTapUp();
    }
    pressedRef.current = false;
    onTouchEnd?.(e);
  };

  const handleTouchCancel = (e: React.TouchEvent<HTMLButtonElement>) => {
    pressedRef.current = false;
    onTouchCancel?.(e);
  };

  return (
    <button
      style={depthStyle}
      disabled={disabled}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      onPointerCancel={handlePointerCancel}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      className={cn(
        'inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm uppercase font-extrabold font-display',

        // Stronger, more reliable transition
        'transition-all duration-200 ease-out',
        'hover:transition-all hover:duration-200 hover:ease-out',

        'disabled:cursor-not-allowed disabled:pointer-events-none select-none',
        styles[variant],
        className,
      )}
      {...rest}
    />
  );
}
